import React, { FC, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Button } from "@shared/components";
import { blurValidationListeners, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { useLoader } from "@shared/hooks";
import { actions as sharedActions } from "@shared/store";
import { actions as SparksActions } from "@containers/Recognition/store/sparks";

// 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3: REUSED, never copied -- PersonSparksDrawerBody's own inline
// reason box imports the SAME schema/constant from this leaf module.
// No longer re-exported here -- DenyRedemptionRequestContainer.test.tsx's own direct-schema test now
// imports from this leaf module too, instead of through this container.
import {
  DENY_REASON_MAX_LENGTH,
  DenyReasonFormShape,
  validationSchema,
} from "../../components/DenyReason/denyReasonValidation";

import "./index.scss";

const defaultValues: DenyReasonFormShape = { reason: "" };

interface DenyRedemptionRequestContainerProps {
  id: number;
  requesterName: string;
  rewardName: string;
  onClose: () => void;
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: the deny overlay's redux-connected body. Composed, not copied, from an
 * existing precedent -- GoalCloseContainer's LAYOUT (heading, Textarea, Cancel/Confirm --
 * Goal/containers/GoalCloseContainer/GoalCloseContainer.tsx) -- plus required, max-length-capped
 * reason validation gating submission. Copies the
 * IDIOM only, never imports from either -- GoalCloseContainer's own comment records its reason field as
 * OPTIONAL (submits blank), which is exactly the posture this form must NOT have.
 *
 * 🚨 Lives in its own top-level directory, not nested under any other container's tree -- kept
 * independent (architecture §6.1) so a folder-proximity deletion elsewhere in this feature can never
 * take it out along with whatever it happens to sit near.
 */
const DenyRedemptionRequestContainer: FC<DenyRedemptionRequestContainerProps> = (props) => {
  const { id, requesterName, rewardName, onClose } = props;
  const dispatch = useDispatch();

  // A dedicated loader name -- "PendingRedemptionRequests" (the panel's own scroll pager) is already
  // taken (RedemptionsContainer.tsx), and useLoader names are global (plan's own note).
  const { isLoading: isSubmitting } = useLoader({
    name: "DenyRedemptionRequest",
    actionTypes: [SparksActions.denyRedemptionRequest],
  });

  /**
   * fix pass F: `isSubmitting` above is redux-derived and only reflects a NEW render -- two clicks
   * inside the same tick (before React has re-rendered with the loader's `disabled` prop) both read the
   * SAME stale `isSubmitting = false` from this render's closure, so the loader alone cannot stop a
   * second POST. This ref is set synchronously inside `handleSubmit` itself, so the second of two
   * same-tick clicks sees it immediately, before any render happens. Reset once the loader clears
   * WITHOUT a success -- a genuine failure leaves this overlay mounted (only `cb`'s `hideOverlay`
   * unmounts it), and a real retry must not stay blocked by the same-tick guard from the first attempt.
   */
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!isSubmitting) {
      isSubmittingRef.current = false;
    }
  }, [isSubmitting]);

  const handleSubmit = useCallback(
    (values: DenyReasonFormShape) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      dispatch(
        SparksActions.denyRedemptionRequest.request({
          id,
          reason: values.reason,
          cb: () => {
            // Dispatched directly, not via the injected `onClose` -- a successful save must always
            // close unconditionally, while routing through `onClose` would invite `leaveConfirm`
            // handling if this form ever grows one, which a successful save has to bypass.
            //
            // 🚨 fix pass G: no page-0 refetch dispatched here any more -- that is now the SINGLE
            // responsibility of `refetchPendingRedemptionRequestsFirstPageSaga` (sparks/saga.tsx),
            // registered on `[approveRedemptionRequest.success, denyRedemptionRequest.success]`, the
            // same way the badge refresh for this event already was.
            dispatch(sharedActions.hideOverlay());
          },
        }),
      );
    },
    [dispatch, id],
  );

  const form = useAppForm({
    defaultValues,
    validators: schemaValidators(validationSchema),
    listeners: blurValidationListeners(),
    onSubmit: ({ value }) => handleSubmit(value),
  });

  return (
    <div className="deny-redemption-request-container">
      <div className="title-wrapper">
        <div className="title">{`Deny ${requesterName}'s request?`}</div>
        <div className="subtitle">{`This returns the held Sparks for "${rewardName}". The reason is shown to ${requesterName}.`}</div>
        <hr />
      </div>
      <form onSubmit={formSubmitHandler(form)}>
        <div className="deny-redemption-request-content">
          <div className="deny-redemption-request-reason-wrapper">
            <form.AppField name="reason">
              {(field) => (
                <field.Textarea
                  className="deny-redemption-request-reason-input"
                  labelTitle="Reason"
                  data-testid="deny-redemption-reason-input"
                  maxLength={DENY_REASON_MAX_LENGTH}
                  placeholder="Why is this request being denied?"
                />
              )}
            </form.AppField>
          </div>
        </div>
        <div className="deny-redemption-request-footer">
          <Button type="button" className="subtle auto-width" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" className="accent padding margin-left" disabled={isSubmitting}>
            Deny request
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DenyRedemptionRequestContainer;
